import { useEffect, useRef, useState } from 'react';
import { WS_ORIGIN } from '@/lib/env';
import { readToken } from '@/auth/token';
import { isPing, type LiveMessage, type Telemetry } from '@/types/api';

export type LiveStatus = 'connecting' | 'live' | 'unauthorized' | 'degraded' | 'offline';

/** Close codes the backend uses deliberately. */
const CLOSE_UNAUTHORIZED = 1008;
const CLOSE_SERVER_ERROR = 1011;

const MAX_RETRIES = 3;

interface UseLiveTelemetryOptions {
  deviceId: number | null;
  /** How many rows to keep for the live table. */
  maxRows?: number;
  enabled?: boolean;
}

/**
 * Live telemetry for one device.
 *
 * The endpoint is mounted at the application root rather than under /api/v1, and
 * the token travels as a query parameter because browsers cannot set headers on a
 * WebSocket handshake. Authorization happens before the socket is accepted, so an
 * unauthorized client never receives a byte — it just gets closed with 1008, which
 * is a permanent answer and must not be retried.
 */
export function useLiveTelemetry({ deviceId, maxRows = 12, enabled = true }: UseLiveTelemetryOptions) {
  const [rows, setRows] = useState<Telemetry[]>([]);
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const retries = useRef(0);
  const socketRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled || deviceId === null) {
      setStatus('offline');
      return;
    }

    let disposed = false;
    retries.current = 0;

    function connect() {
      const token = readToken();
      if (!token) {
        setStatus('unauthorized');
        return;
      }

      setStatus('connecting');
      const socket = new WebSocket(
        `${WS_ORIGIN}/ws/telemetry/${deviceId}?token=${encodeURIComponent(token)}`
      );
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed) return;
        retries.current = 0;
        setStatus('live');
      };

      socket.onmessage = (event) => {
        if (disposed) return;
        let message: LiveMessage;
        try {
          message = JSON.parse(event.data as string) as LiveMessage;
        } catch {
          return;
        }

        // Keepalives arrive every 30s of silence and are not data.
        if (isPing(message)) return;

        setRows((current) => [message, ...current].slice(0, maxRows));
      };

      socket.onclose = (event) => {
        if (disposed) return;

        if (event.code === CLOSE_UNAUTHORIZED) {
          // Not ours to view. Reconnecting would just be refused again.
          setStatus('unauthorized');
          return;
        }

        if (event.code === CLOSE_SERVER_ERROR) {
          // Redis is down: live streaming is unavailable, but the polled position
          // on the same page still works, so this is degraded rather than broken.
          setStatus('degraded');
          return;
        }

        if (retries.current >= MAX_RETRIES) {
          setStatus('degraded');
          return;
        }

        retries.current += 1;
        const delay = Math.min(1000 * 2 ** retries.current, 8000);
        setStatus('connecting');
        timerRef.current = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      disposed = true;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [deviceId, enabled, maxRows]);

  return { rows, status, latest: rows[0] ?? null };
}
